import React, { useEffect, useState } from 'react';
import { useLandlordStore } from '@/stores/useLandlordStore';
import { Search, Filter, Phone, Mail, Home, MoreVertical, Plus } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TenantInviteModal } from './TenantInviteModal';

export const TenantList: React.FC = () => {
    const { tenants, fetchTenants, loading } = useLandlordStore();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [searchTerm, setSearchTerm] = useState('');
    const filterOptions = ['All', 'Active', 'Past', 'Pending'] as const;
    const [statusFilter, setStatusFilter] = useState<typeof filterOptions[number]>('All');
    const [selectedPropertyId, setSelectedPropertyId] = useState<string>(searchParams.get('propertyId') || 'All');
    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

    useEffect(() => {
        const propertyId = searchParams.get('propertyId');
        if (propertyId) {
            setSelectedPropertyId(propertyId);
        }
    }, [searchParams]);

    useEffect(() => {
        fetchTenants();
    }, []);

    const filteredTenants = tenants.filter(tenant => {
        const matchesSearch = tenant.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            tenant.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            tenant.unit_number?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus = statusFilter === 'All' || tenant.status === statusFilter;

        const propertyId = (tenant as any).property_id || (tenant as any).propertyId || tenant.property_name;
        const matchesProperty = selectedPropertyId === 'All' || (propertyId && propertyId === selectedPropertyId);

        return matchesSearch && matchesStatus && matchesProperty;
    });

    const tenantsToShow = filteredTenants;

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-light-text dark:text-dark-text dark:text-white">Tenants</h1>
                    <p className="text-sm text-light-subtle dark:text-dark-subtle">Manage your tenants and lease agreements</p>
                </div>

                <div className="flex items-center gap-3">
                    <div className="relative w-[220px] md:w-[280px]">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle" size={16} />
                        <input
                            type="text"
                            placeholder="Search tenants"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-9 pr-3 py-2 rounded-full border border-light-border dark:border-dark-border bg-light-bg dark:bg-dark-bg text-sm text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
                        />
                    </div>

                    <button
                        onClick={() => setIsInviteModalOpen(true)}
                        className="w-10 h-10 grid place-items-center rounded-full bg-primary hover:bg-primary/90 text-white transition-colors"
                        aria-label="Invite Tenant"
                    >
                        <Plus size={18} />
                    </button>
                </div>
            </div>

            {/* Filters (search moved to top bar, chips in line) */}
            <div className="flex flex-wrap items-center gap-2">
                {filterOptions.map((option) => {
                    const isActive = statusFilter === option;
                    return (
                        <button
                            key={option}
                            onClick={() => setStatusFilter(option)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${isActive ? 'bg-primary text-white' : 'bg-light-bg dark:bg-dark-bg text-light-text dark:text-dark-text border border-light-border dark:border-dark-border hover:bg-light-card dark:hover:bg-dark-card'}`}
                        >
                            {option}
                        </button>
                    );
                })}

                <button
                    type="button"
                    onClick={() => navigate('/landlord/tenants/invitations')}
                    className="ml-2 px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-2 text-light-text dark:text-dark-text bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border hover:bg-light-card dark:hover:bg-dark-card"
                >
                    <Mail size={12} />
                    Pending invites
                </button>
            </div>

            {/* Tenant Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {loading ? (
                    <div className="col-span-full py-12 text-center text-light-subtle dark:text-dark-subtle">Loading tenants...</div>
                ) : tenantsToShow.length === 0 ? (
                    <div className="col-span-full py-12 text-center text-light-subtle dark:text-dark-subtle">No tenants found matching your criteria.</div>
                ) : (
                    tenantsToShow.map((tenant) => (
                        <motion.button
                            key={tenant.id}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            onClick={() => navigate(`/landlord/tenants/${tenant.user_id || tenant.id}`)}
                            className="text-left bg-light-card dark:bg-dark-card rounded-xl border border-light-border/30 dark:border-dark-border/30 shadow-sm hover:shadow-md transition-all p-5 w-full flex flex-col justify-between group"
                        >
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <p className="text-lg font-semibold text-primary">{tenant.full_name}</p>
                                    <p className="text-xs text-light-subtle dark:text-dark-subtle mt-1">ID: {(tenant.user_id || tenant.id).substring(0, 8)}</p>
                                </div>
                                <span
                                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                                        tenant.status === 'Active'
                                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                            : tenant.status === 'Pending'
                                                ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                                                : 'bg-gray-100 dark:bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-light-subtle dark:text-dark-subtle'
                                    }`}
                                >
                                    {tenant.status}
                                </span>
                            </div>

                            <div className="mt-4 grid grid-cols-1 gap-3 text-sm text-light-subtle dark:text-dark-subtle">
                                <div className="flex items-center gap-2">
                                    <Home size={16} />
                                    <span>{tenant.property_name || 'No Property'}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Home size={16} />
                                    <span>{tenant.unit_number || 'No Unit'}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Mail size={16} />
                                    <span>{tenant.email}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Phone size={16} />
                                    <span>{tenant.phone_number}</span>
                                </div>
                            </div>

                            <div className="mt-4 flex items-center justify-between text-sm">
                                <span className="font-medium text-primary">View Profile</span>
                                <span className="text-light-subtle dark:text-dark-subtle">›</span>
                            </div>
                        </motion.button>
                    ))
                )}
            </div>

            <TenantInviteModal
                isOpen={isInviteModalOpen}
                onClose={() => setIsInviteModalOpen(false)}
            />
        </div>
    );
};
